/* Curate Your Own Set — Terre de Mars
   Tiered set builder: pick N products, unlock a % discount, add all to cart. */

class CurateSet extends HTMLElement {
  connectedCallback() {
    this.strings = this.readJSON('[data-curate-strings]') || {};
    this.locale = this.dataset.locale || 'fr';
    this.currency = this.dataset.currency || 'EUR';
    this.minItems = parseInt(this.dataset.minItems, 10) || 3;
    this.persist = this.dataset.persist === 'true';
    this.storageKey = this.dataset.storageKey;
    this.redirectToCart = this.dataset.redirectToCart === 'true';

    this.tiers = this.parseTiers();
    this.maxTierQty = this.tiers.length ? Math.max(...this.tiers.map((t) => t.qty)) : this.minItems;
    this.slotCount = Math.max(this.minItems, this.maxTierQty);

    this.products = this.collectProducts();
    this.selection = this.restore(); // [{variantId, productId}]

    this.grid = this.querySelector('[data-curate-grid]');
    this.slotsEl = this.querySelector('[data-curate-slots]');
    this.progressEl = this.querySelector('[data-curate-progress]');
    this.totalEl = this.querySelector('[data-curate-total]');
    this.compareEl = this.querySelector('[data-curate-compare]');
    this.summaryLabelEl = this.querySelector('[data-curate-summary-label]');
    this.submitEl = this.querySelector('[data-curate-submit]');
    this.ctaLabelEl = this.querySelector('[data-curate-cta-label]');
    this.errorEl = this.querySelector('[data-curate-error]');
    this.countEl = this.querySelector('[data-curate-count]');
    this.defaultTotalLabel = this.summaryLabelEl ? this.summaryLabelEl.textContent.trim() : 'Total';
    this.dockEl = this.querySelector('[data-curate-dock]');

    this.bind();
    this.render();
  }

  /* ---------- setup helpers ---------- */

  readJSON(sel, root = this) {
    const el = root.querySelector(sel);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[curate-set] bad JSON in', sel, e);
      return null;
    }
  }

  parseTiers() {
    const qtys = (this.dataset.tierQtys || '').split(',').filter(Boolean).map(Number);
    const pcts = (this.dataset.tierPcts || '').split(',').filter(Boolean).map(Number);
    const codes = (this.dataset.tierCodes || '').split(',');
    return qtys
      .map((qty, i) => ({ qty, pct: pcts[i] || 0, code: (codes[i] || '').trim() }))
      .filter((t) => t.qty > 0)
      .sort((a, b) => a.qty - b.qty);
  }

  collectProducts() {
    const map = new Map();
    this.querySelectorAll('[data-curate-card]').forEach((card) => {
      const data = this.readJSON('[data-curate-product]', card);
      if (!data) return;
      data.el = card;
      data.defaultVariantId = (data.variants.find((v) => v.available) || data.variants[0]).id;
      data.selectedVariantId = data.defaultVariantId;
      map.set(String(data.id), data);
      data.variants.forEach((v) => this.variantIndex.set(String(v.id), { product: data, variant: v }));
    });
    return map;
  }

  get variantIndex() {
    if (!this._variantIndex) this._variantIndex = new Map();
    return this._variantIndex;
  }

  restore() {
    if (!this.persist) return [];
    try {
      const raw = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
      return raw.filter((entry) => this.variantIndex.has(String(entry.variantId)));
    } catch (e) {
      return [];
    }
  }

  save() {
    if (!this.persist) return;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.selection));
    } catch (e) {
      /* quota / private mode — non fatal */
    }
  }

  /* ---------- events ---------- */

  bind() {
    this.addEventListener('click', (e) => {
      const filter = e.target.closest('[data-filter]');
      if (filter) return this.onFilter(filter);

      const variantBtn = e.target.closest('[data-variant-id]');
      if (variantBtn && variantBtn.closest('[data-curate-variants]')) return this.onVariantPick(variantBtn);

      const toggle = e.target.closest('[data-curate-toggle]');
      if (toggle) return this.onToggle(toggle.closest('[data-curate-card]'));

      const removeBtn = e.target.closest('[data-curate-remove]');
      if (removeBtn) return this.removeAt(parseInt(removeBtn.dataset.curateRemove, 10));

      const submit = e.target.closest('[data-curate-submit]');
      if (submit) return this.submit();

      const dockOpen = e.target.closest('[data-curate-dock-open]');
      if (dockOpen) return this.querySelector('.curate__panel').scrollIntoView({ behavior: 'smooth' });
    });
  }

  onFilter(btn) {
    this.querySelectorAll('[data-filter]').forEach((b) => {
      const on = b === btn;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', String(on));
    });
    const key = btn.dataset.filter;
    let visible = 0;
    this.querySelectorAll('[data-curate-card]').forEach((card) => {
      const show = key === 'all' || (card.dataset.groups || '').split(/\s+/).includes(key);
      card.hidden = !show;
      if (show) visible += 1;
    });
    if (this.countEl) this.countEl.textContent = `${visible} ${this.strings.productsWord || ''}`.trim();

    this.applyVariantRule(btn.dataset.variantMatch);
    this.render();
  }

  // Titles are inconsistent across products ("5L.", "5L. + FLACON", "5L.+ flacon Offert"),
  // so compare case- and whitespace-insensitively rather than on the exact string.
  normalize(str) {
    return String(str || '').toLowerCase().replace(/\s+/g, '');
  }

  /* A filter tab may force a format (Recharges -> 5L., Formats voyage -> smallest size).
     `match` is an ordered, comma-separated fallback chain: the first term with an
     available variant wins. Order matters — "50ml" is a substring of "150ml", so
     "150ml" has to be tried first. A tab with no rule restores each card's default
     variant, so a format forced on one tab never leaks into the others.
     Products matching nothing keep their current selection. */
  applyVariantRule(match) {
    const needles = String(match || '')
      .split(',')
      .map((n) => this.normalize(n))
      .filter(Boolean);

    this.products.forEach((product) => {
      if (product.el.hidden) return;

      let target = null;
      if (needles.length) {
        for (let i = 0; i < needles.length; i += 1) {
          target = product.variants.find((v) => v.available && this.normalize(v.title).includes(needles[i]));
          if (target) break;
        }
      } else {
        target = product.variants.find((v) => v.id === product.defaultVariantId) || null;
      }

      if (!target || target.id === product.selectedVariantId) return;
      this.selectVariant(product, target);
    });

    this.dedupeSelection();
    this.save();
  }

  selectVariant(product, target) {
    const previousId = product.selectedVariantId;
    product.selectedVariantId = target.id;
    product.el
      .querySelectorAll('[data-variant-id]')
      .forEach((b) => b.classList.toggle('is-selected', Number(b.dataset.variantId) === target.id));

    // Keep the set in sync, otherwise the card pill and the panel disagree.
    this.selection.forEach((entry) => {
      if (entry.productId === product.id && entry.variantId === previousId) entry.variantId = target.id;
    });
  }

  // A swap can collide with a variant of the same product already in the set.
  dedupeSelection() {
    const seen = new Set();
    this.selection = this.selection.filter((entry) => {
      if (seen.has(entry.variantId)) return false;
      seen.add(entry.variantId);
      return true;
    });
  }

  onVariantPick(btn) {
    const card = btn.closest('[data-curate-card]');
    const product = this.products.get(card.dataset.productId);
    if (!product) return;
    product.selectedVariantId = Number(btn.dataset.variantId);
    card.querySelectorAll('[data-variant-id]').forEach((b) => b.classList.toggle('is-selected', b === btn));
    this.render();
  }

  onToggle(card) {
    const product = this.products.get(card.dataset.productId);
    if (!product) return;
    const variantId = product.selectedVariantId;
    const idx = this.selection.findIndex((s) => s.variantId === variantId);
    if (idx > -1) this.selection.splice(idx, 1);
    else this.selection.push({ variantId, productId: product.id });
    this.save();
    this.render();
  }

  removeAt(index) {
    this.selection.splice(index, 1);
    this.save();
    this.render();
  }

  /* ---------- pricing ---------- */

  currentTier() {
    return this.tiers.filter((t) => this.selection.length >= t.qty).pop() || null;
  }

  nextTier() {
    return this.tiers.find((t) => this.selection.length < t.qty) || null;
  }

  totals() {
    const subtotal = this.selection.reduce((sum, s) => {
      const entry = this.variantIndex.get(String(s.variantId));
      return sum + (entry ? entry.variant.price : 0);
    }, 0);
    const tier = this.currentTier();
    const pct = tier ? tier.pct : 0;
    const total = Math.round(subtotal * (1 - pct / 100));
    return { subtotal, total, pct, tier };
  }

  money(cents) {
    return new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
  }

  pctLabel(pct) {
    return `${pct}%`;
  }

  // Placeholders use %token% — braces would break Liquid's lexer inside {{ ... }}.
  fill(tpl, vars) {
    return String(tpl || '').replace(/%(\w+)%/g, (_, k) => (vars[k] !== undefined ? vars[k] : ''));
  }

  /* ---------- render ---------- */

  render() {
    this.renderCards();
    this.renderProgress();
    this.renderSlots();
    this.renderSummary();
    this.renderDock();
  }

  renderCards() {
    this.products.forEach((product) => {
      const selected = this.selection.some((s) => s.variantId === product.selectedVariantId);
      const toggle = product.el.querySelector('[data-curate-toggle]');
      toggle.setAttribute('aria-pressed', String(selected));
      product.el.classList.toggle('is-selected', selected);

      const variant = product.variants.find((v) => v.id === product.selectedVariantId);
      if (!variant) return;
      const titleEl = product.el.querySelector('[data-curate-variant-title]');
      const priceEl = product.el.querySelector('[data-curate-variant-price]');
      if (titleEl) titleEl.textContent = product.variants.length > 1 ? `${variant.title} | ` : '';
      if (priceEl) priceEl.textContent = this.money(variant.price);
      this.swapImage(product, variant);
    });
  }

  /* Card art follows the selected variant. Liquid rendered a responsive srcset for
     the featured image; drop it on the first swap or the browser keeps picking from it. */
  swapImage(product, variant) {
    const img = product.el.querySelector('.curate-card__img');
    const url = variant.image || product.image;
    if (!img || !url || img.getAttribute('src') === url) return;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.setAttribute('src', url);
    img.setAttribute('alt', product.variants.length > 1 ? `${product.title} — ${variant.title}` : product.title);
  }

  renderProgress() {
    if (!this.progressEl) return;
    const n = this.selection.length;
    const steps = [];
    for (let i = 1; i <= this.slotCount; i += 1) {
      const tier = this.tiers.find((t) => t.qty === i);
      steps.push(`
        <li class="curate__step${n >= i ? ' is-reached' : ''}">
          <span class="curate__step-dot">${n >= i ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12.5l5.5 5.5L20 7"/></svg>' : ''}</span>
          <span class="curate__step-label">${tier ? this.fill(this.strings.off, { percent: this.pctLabel(tier.pct) }) : ''}</span>
        </li>`);
    }
    const reached = Math.min(n, this.slotCount);
    const pctBar = this.slotCount > 1 ? ((reached - 1) / (this.slotCount - 1)) * 100 : 0;
    this.progressEl.style.setProperty('--curate-progress', `${Math.max(0, pctBar)}%`);
    this.progressEl.innerHTML = steps.join('');
  }

  renderSlots() {
    if (!this.slotsEl) return;
    const filled = this.selection.map((s, i) => {
      const { product, variant } = this.variantIndex.get(String(s.variantId));
      const img = variant.image || product.image;
      return `
        <li class="curate__slot is-filled">
          <button type="button" class="curate__slot-remove" data-curate-remove="${i}"
                  aria-label="${this.strings.remove || 'Remove'} ${this.escape(product.title)}">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
          </button>
          <span class="curate__slot-media">
            ${img ? `<img src="${img}" alt="" loading="lazy" width="160" height="160">` : ''}
          </span>
          <span class="curate__slot-title">${this.escape(product.title)}</span>
          <span class="curate__slot-variant">${product.variants.length > 1 ? this.escape(variant.title) : ''}</span>
        </li>`;
    });

    const empties = [];
    for (let i = this.selection.length; i < this.slotCount; i += 1) {
      const tier = this.tiers.find((t) => t.qty === i + 1);
      empties.push(`
        <li class="curate__slot">
          <span class="curate__slot-media curate__slot-media--empty"></span>
          <span class="curate__slot-title">${this.escape(this.strings.addItem || '')}</span>
          <span class="curate__slot-variant">${
            tier ? this.escape(this.fill(this.strings.unlock, { percent: this.pctLabel(tier.pct) })) : ''
          }</span>
        </li>`);
    }

    this.slotsEl.innerHTML = filled.join('') + empties.join('');
  }

  renderSummary() {
    const { subtotal, total, pct } = this.totals();
    const missing = Math.max(0, this.minItems - this.selection.length);

    if (missing > 0) {
      this.summaryLabelEl.textContent = this.fill(this.strings.addN, { n: missing });
      this.totalEl.textContent = this.money(subtotal);
      this.compareEl.hidden = true;
      this.submitEl.disabled = true;
      this.ctaLabelEl.textContent = this.fill(this.strings.ctaIncomplete, { n: missing });
    } else {
      this.summaryLabelEl.textContent = this.defaultTotalLabel;
      this.totalEl.textContent = this.money(total);
      this.compareEl.hidden = pct === 0;
      this.compareEl.textContent = this.money(subtotal);
      this.submitEl.disabled = false;
      this.ctaLabelEl.textContent = this.strings.ctaReady;
    }
  }

  renderDock() {
    if (!this.dockEl) return;
    const { total } = this.totals();
    this.dockEl.hidden = this.selection.length === 0;
    this.dockEl.querySelector('[data-curate-dock-count]').textContent = String(this.selection.length);
    this.dockEl.querySelector('[data-curate-dock-total]').textContent = this.money(total);
  }

  escape(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  /* ---------- submit ---------- */

  async submit() {
    if (this.selection.length < this.minItems) return;
    this.setLoading(true);
    this.errorEl.hidden = true;

    // Merge duplicates into quantities
    const byVariant = new Map();
    this.selection.forEach((s) => byVariant.set(s.variantId, (byVariant.get(s.variantId) || 0) + 1));

    const tier = this.currentTier();
    const items = [...byVariant.entries()].map(([id, quantity]) => ({
      id,
      quantity,
      properties: { _curated_set: tier ? `${this.selection.length} · ${tier.pct}%` : String(this.selection.length) },
    }));

    try {
      const res = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error((await res.json()).description || res.statusText);

      this.selection = [];
      this.save();

      const code = tier && tier.code;
      if (code) {
        // Applies the discount to the session, then lands on the cart.
        window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(this.dataset.cartUrl)}`;
        return;
      }
      if (this.redirectToCart) {
        window.location.href = this.dataset.cartUrl;
        return;
      }
      document.dispatchEvent(new CustomEvent('curate-set:added'));
      this.render();
    } catch (err) {
      this.errorEl.textContent = err.message || this.strings.error;
      this.errorEl.hidden = false;
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(state) {
    this.submitEl.classList.toggle('is-loading', state);
    this.submitEl.disabled = state || this.selection.length < this.minItems;
    this.submitEl.querySelector('.curate__spinner').hidden = !state;
  }
}

customElements.define('curate-set', CurateSet);
